import React, { useState, useEffect, useRef } from "react";
import Sidebar from "./components/Sidebar";
import API_BASE_URL from "./config";
import "./MyWebsite.css";

const WA_CODES = [
  { code: "91",  flag: "🇮🇳", name: "India" },
  { code: "1",   flag: "🇺🇸", name: "US / Canada" },
  { code: "44",  flag: "🇬🇧", name: "UK" },
  { code: "971", flag: "🇦🇪", name: "UAE" },
  { code: "65",  flag: "🇸🇬", name: "Singapore" },
  { code: "60",  flag: "🇲🇾", name: "Malaysia" },
  { code: "61",  flag: "🇦🇺", name: "Australia" },
  { code: "27",  flag: "🇿🇦", name: "South Africa" },
  { code: "92",  flag: "🇵🇰", name: "Pakistan" },
  { code: "880", flag: "🇧🇩", name: "Bangladesh" },
  { code: "94",  flag: "🇱🇰", name: "Sri Lanka" },
  { code: "977", flag: "🇳🇵", name: "Nepal" },
];

// Detect country code from browser timezone — no network call
function detectCountryCode() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const map = {
      "Asia/Kolkata": "91", "Asia/Calcutta": "91",
      "Asia/Dubai": "971", "Asia/Muscat": "971",
      "Asia/Singapore": "65",
      "Asia/Kuala_Lumpur": "60", "Asia/Kuching": "60",
      "Asia/Colombo": "94",
      "Asia/Kathmandu": "977",
      "Asia/Dhaka": "880",
      "Asia/Karachi": "92",
      "Africa/Johannesburg": "27",
      "Australia/Sydney": "61", "Australia/Melbourne": "61",
      "Australia/Brisbane": "61", "Australia/Perth": "61",
      "Europe/London": "44",
      "America/New_York": "1", "America/Chicago": "1",
      "America/Los_Angeles": "1", "America/Toronto": "1",
      "America/Vancouver": "1",
    };
    return map[tz] || "91";
  } catch {
    return "91";
  }
}

// Split a saved full number (e.g. "919876543210") back into { code, phone }
function parseWaNumber(full) {
  const digits = (full || "").replace(/\D/g, "");
  const sorted = ["971", "977", "880", "65", "92", "94", "61", "60", "44", "27", "91", "1"];
  for (const c of sorted) {
    if (digits.startsWith(c)) return { whatsappCountryCode: c, whatsappPhone: digits.slice(c.length) };
  }
  return { whatsappCountryCode: "91", whatsappPhone: digits };
}

const INHOUSE_TEMPLATES = [
  {
    slug: "light-airy",
    name: "Light & Airy",
    desc: "Celebrations, birthdays, family",
    palette: ["#faf8f5", "#e8d5c8", "#c4856a"],
  },
  {
    slug: "corporate-events",
    name: "Corporate Events",
    desc: "Conferences, product launches",
    palette: ["#1a2744", "#2d4a8a", "#ffffff"],
  },
  {
    slug: "festival-nights",
    name: "Festival Nights",
    desc: "Concerts, cultural, nightlife",
    palette: ["#0d0d0d", "#1a1a1a", "#ff6b35"],
  },
  {
    slug: "candid-editorial",
    name: "Candid Editorial",
    desc: "Documentary, journalism style",
    palette: ["#f0ede8", "#1a1a1a", "#888888"],
  },
];

function MyWebsite() {
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [loading, setLoading] = useState(true);

  // Which mode we're in
  const [mode, setMode] = useState(null); // "inhouse" | "wp" | "none"

  // In-house site data
  const [siteData, setSiteData] = useState(null);

  // WP legacy data (for existing studios)
  const [wpData, setWpData] = useState(null);

  // Editor state
  const [activeTab, setActiveTab] = useState("content");
  const [form, setForm] = useState({
    studioName: "", headline: "", subheadline: "", aboutText: "",
    contactEmail: "", contactPhone: "",
    whatsappCountryCode: detectCountryCode(), whatsappPhone: "",
    socialInstagram: "", socialFacebook: "",
    templateSlug: "",
  });
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");

  // Image upload state
  const [uploading, setUploading] = useState({ hero: false, about: false, gallery: false });
  const heroRef = useRef();
  const aboutRef = useRef();
  const galleryRef = useRef();

  // Gallery drag-and-drop reorder
  const dragIdxRef = useRef(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  // Template picker (new users)
  const [selectedTemplate, setSelectedTemplate] = useState("light-airy");
  const [provisioning, setProvisioning] = useState(false);

  // WP editor state (legacy)
  const [openingEditor, setOpeningEditor] = useState(false);

  const [message, setMessage] = useState("");
  const token = localStorage.getItem("token");

  // ─── Load ───────────────────────────────────────────────────────────────────

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        // Check in-house first
        const ihRes = await fetch(`${API_BASE_URL}/api/inhouse-website/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const ihData = await ihRes.json();
        if (ihData.hasWebsite) {
          setSiteData(ihData);
          setForm({
            studioName: ihData.content?.studioName || "",
            headline: ihData.content?.headline || "",
            subheadline: ihData.content?.subheadline || "",
            aboutText: ihData.content?.aboutText || "",
            contactEmail: ihData.content?.contactEmail || "",
            contactPhone: ihData.content?.contactPhone || "",
            ...parseWaNumber(ihData.content?.whatsappNumber || ""),
            socialInstagram: ihData.content?.socialInstagram || "",
            socialFacebook: ihData.content?.socialFacebook || "",
            templateSlug: ihData.templateSlug || "light-airy",
          });
          setMode("inhouse");
          return;
        }

        // Fall back to legacy WP
        const wpRes = await fetch(`${API_BASE_URL}/api/website/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const wp = await wpRes.json();
        if (wp.hasWebsite) {
          setWpData(wp);
          setMode("wp");
          return;
        }

        setMode("none");
      } catch {
        setMessage("Failed to load website status");
        setMode("none");
      } finally {
        setLoading(false);
      }
    }
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Provision ──────────────────────────────────────────────────────────────

  const handleProvision = async () => {
    setProvisioning(true);
    setMessage("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/inhouse-website/provision`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ templateSlug: selectedTemplate }),
      });
      const data = await res.json();
      if (res.ok) {
        // Reload full status
        window.location.reload();
      } else {
        setMessage(data.message || "Failed to create website");
      }
    } catch {
      setMessage("Error connecting to server");
    } finally {
      setProvisioning(false);
    }
  };

  // ─── Save content ────────────────────────────────────────────────────────────

  const handleSave = async () => {
    setSaving(true);
    setSavedMsg("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/inhouse-website/content`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...form,
          whatsappNumber: form.whatsappPhone.replace(/\D/g, "")
            ? form.whatsappCountryCode + form.whatsappPhone.replace(/\D/g, "")
            : "",
        }),
      });
      if (res.ok) {
        setSavedMsg("Saved!");
        setTimeout(() => setSavedMsg(""), 2500);
      } else {
        const d = await res.json();
        setMessage(d.message || "Save failed");
      }
    } catch {
      setMessage("Error connecting to server");
    } finally {
      setSaving(false);
    }
  };

  // ─── Image upload ────────────────────────────────────────────────────────────

  // Resize to max 1920px on the longest side before encoding — cuts payload by 5-10×
  const resizeAndEncode = (file, maxPx = 1920) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = (ev) => {
        const img = new Image();
        img.onerror = reject;
        img.onload = () => {
          const scale = Math.min(1, maxPx / Math.max(img.width, img.height));
          const w = Math.round(img.width * scale);
          const h = Math.round(img.height * scale);
          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h;
          canvas.getContext("2d").drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL("image/jpeg", 0.85));
        };
        img.src = ev.target.result;
      };
      reader.readAsDataURL(file);
    });

  const uploadImage = async (file, type) => {
    setUploading((u) => ({ ...u, [type]: true }));
    try {
      const imageData = await resizeAndEncode(file);
      const res = await fetch(`${API_BASE_URL}/api/inhouse-website/upload-image`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ imageData, type }),
      });
      const data = await res.json();
      if (res.ok && data.url) {
        // Update state directly from the returned URL — no extra network round-trip
        setSiteData((prev) => {
          const content = { ...(prev?.content || {}) };
          if (type === "hero") content.heroImageUrl = data.url;
          else if (type === "about") content.aboutImageUrl = data.url;
          else if (type === "gallery")
            content.galleryImageUrls = [...(content.galleryImageUrls || []), data.url];
          return { ...prev, content };
        });
      } else {
        setMessage(data.message || "Upload failed");
      }
    } catch {
      setMessage("Upload error");
    } finally {
      setUploading((u) => ({ ...u, [type]: false }));
    }
  };

  const handleFileInput = (type, ref) => {
    ref.current?.click();
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    uploadImage(file, type);
    e.target.value = "";
  };

  const handleGalleryChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading((u) => ({ ...u, gallery: true }));
    for (const file of files) {
      await uploadImage(file, "gallery");
    }
    setUploading((u) => ({ ...u, gallery: false }));
    e.target.value = "";
  };

  const removeGalleryImage = async (url) => {
    try {
      await fetch(`${API_BASE_URL}/api/inhouse-website/gallery-image`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ url }),
      });
      setSiteData((prev) => ({
        ...prev,
        content: {
          ...prev.content,
          galleryImageUrls: (prev.content?.galleryImageUrls || []).filter((u) => u !== url),
        },
      }));
    } catch {
      setMessage("Failed to remove image");
    }
  };

  // ─── Gallery drag-and-drop reorder ───────────────────────────────────────────

  const handleGalleryDragStart = (i) => {
    dragIdxRef.current = i;
  };

  const handleGalleryDragOver = (e, i) => {
    e.preventDefault();
    if (dragOverIdx !== i) setDragOverIdx(i);
  };

  const handleGalleryDrop = async (e, dropIdx) => {
    e.preventDefault();
    const dragIdx = dragIdxRef.current;
    setDragOverIdx(null);
    dragIdxRef.current = null;
    if (dragIdx === null || dragIdx === dropIdx) return;

    const current = siteData?.content?.galleryImageUrls || [];
    const reordered = [...current];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(dropIdx, 0, moved);

    setSiteData((prev) => ({
      ...prev,
      content: { ...prev.content, galleryImageUrls: reordered },
    }));

    try {
      await fetch(`${API_BASE_URL}/api/inhouse-website/gallery-order`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ urls: reordered }),
      });
    } catch {}
  };

  const handleGalleryDragEnd = () => {
    dragIdxRef.current = null;
    setDragOverIdx(null);
  };

  // ─── WP legacy: SSO ──────────────────────────────────────────────────────────

  const handleWpEdit = async () => {
    // The blank-tab-then-redirect trick exists to survive popup blockers on the
    // web. In the desktop app there is no popup to pre-open — window.open("")
    // is denied, `tab` comes back null, and the editor simply never opened. On
    // desktop we skip straight to handing the finished URL to the browser.
    const tab = window.photentra?.isDesktop ? null : window.open("", "_blank");
    setOpeningEditor(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/website/sso-login`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.url && /^https?:\/\//i.test(data.url)) {
        if (tab) tab.location.href = data.url;
        else if (window.photentra?.openExternal) window.photentra.openExternal(data.url);
      } else {
        if (tab) tab.close();
        setMessage(data.message || "Failed to open editor");
      }
    } catch {
      if (tab) tab.close();
      setMessage("Error connecting to server");
    } finally {
      setOpeningEditor(false);
    }
  };

  // ─── Renders ─────────────────────────────────────────────────────────────────

  const renderTemplatePicker = () => (
    <div className="mw-empty">
      <h2>Create Your Event Photography Website</h2>
      <p>Choose a theme and go live in seconds. You can change it anytime.</p>
      <div className="mw-template-grid">
        {INHOUSE_TEMPLATES.map((t) => (
          <div
            key={t.slug}
            className={`mw-template-card ${selectedTemplate === t.slug ? "mw-template-selected" : ""}`}
            onClick={() => setSelectedTemplate(t.slug)}
          >
            <div className="mw-template-swatch">
              {t.palette.map((c, i) => (
                <div key={i} className="mw-swatch-block" style={{ background: c }} />
              ))}
              {selectedTemplate === t.slug && <div className="mw-template-check">✓</div>}
            </div>
            <div className="mw-template-label">
              <strong>{t.name}</strong>
              <span>{t.desc}</span>
            </div>
          </div>
        ))}
      </div>
      <button
        className="mw-create-btn"
        disabled={provisioning}
        onClick={handleProvision}
      >
        {provisioning ? "Creating…" : "Launch My Website →"}
      </button>
    </div>
  );

  const renderEditor = () => {
    const siteUrl = siteData?.siteUrl;
    const gallery = siteData?.content?.galleryImageUrls || [];
    const heroUrl = siteData?.content?.heroImageUrl;
    const aboutUrl = siteData?.content?.aboutImageUrl;
    const currentTpl = INHOUSE_TEMPLATES.find((t) => t.slug === form.templateSlug);

    return (
      <div className="mw-editor">
        {/* Top bar */}
        <div className="mw-editor-topbar">
          <div className="mw-editor-domain">
            <span className="mw-domain-pill">{siteData?.studioSlug}.photentra.com</span>
          </div>
          <div className="mw-editor-actions">
            {savedMsg && <span className="mw-saved-badge">{savedMsg}</span>}
            <a href={siteUrl} target="_blank" rel="noopener noreferrer" className="mw-btn mw-btn-outline">
              View Site
            </a>
            <button className="mw-btn mw-btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mw-tabs">
          {["content", "photos", "contact", "template"].map((tab) => (
            <button
              key={tab}
              className={`mw-tab ${activeTab === tab ? "mw-tab-active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Tab panels */}
        <div className="mw-tab-panel">

          {activeTab === "content" && (
            <div className="mw-form">
              <div className="mw-field">
                <label>Studio Name</label>
                <input value={form.studioName} onChange={(e) => setForm({ ...form, studioName: e.target.value })} placeholder="e.g. Riya Photography" />
              </div>
              <div className="mw-field">
                <label>Headline</label>
                <input value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} placeholder="e.g. Capturing Your Special Moments" />
              </div>
              <div className="mw-field">
                <label>Subheadline</label>
                <input value={form.subheadline} onChange={(e) => setForm({ ...form, subheadline: e.target.value })} placeholder="e.g. Professional event photography across Mumbai" />
              </div>
              <div className="mw-field">
                <label>About Text</label>
                <textarea rows={5} value={form.aboutText} onChange={(e) => setForm({ ...form, aboutText: e.target.value })} placeholder="Tell visitors about your studio, your style, your experience…" />
              </div>
            </div>
          )}

          {activeTab === "photos" && (
            <div className="mw-photos">
              {/* Hidden file inputs */}
              <input ref={heroRef} type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => handleFileChange(e, "hero")} />
              <input ref={aboutRef} type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => handleFileChange(e, "about")} />
              <input ref={galleryRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={handleGalleryChange} />

              <div className="mw-photo-row">
                <div className="mw-photo-slot">
                  <div className="mw-photo-label">Hero Image <span>(shown full-screen on homepage)</span></div>
                  <div className="mw-photo-preview" onClick={() => handleFileInput("hero", heroRef)}>
                    {heroUrl
                      ? <img src={heroUrl} alt="Hero" />
                      : <div className="mw-photo-placeholder">{uploading.hero ? "Uploading…" : "+ Upload hero image"}</div>}
                  </div>
                  {heroUrl && (
                    <button className="mw-photo-change" onClick={() => handleFileInput("hero", heroRef)}>
                      {uploading.hero ? "Uploading…" : "Change image"}
                    </button>
                  )}
                </div>

                <div className="mw-photo-slot">
                  <div className="mw-photo-label">About Image <span>(shown beside your bio)</span></div>
                  <div className="mw-photo-preview" onClick={() => handleFileInput("about", aboutRef)}>
                    {aboutUrl
                      ? <img src={aboutUrl} alt="About" />
                      : <div className="mw-photo-placeholder">{uploading.about ? "Uploading…" : "+ Upload about image"}</div>}
                  </div>
                  {aboutUrl && (
                    <button className="mw-photo-change" onClick={() => handleFileInput("about", aboutRef)}>
                      {uploading.about ? "Uploading…" : "Change image"}
                    </button>
                  )}
                </div>
              </div>

              <div className="mw-gallery-section">
                <div className="mw-photo-label">Gallery <span>({gallery.length} photos)</span></div>
                <div className="mw-gallery-grid">
                  {gallery.map((url, i) => (
                    <div
                      key={url}
                      className={`mw-gallery-thumb${dragOverIdx === i ? " mw-gallery-dragover" : ""}`}
                      draggable
                      onDragStart={() => handleGalleryDragStart(i)}
                      onDragOver={(e) => handleGalleryDragOver(e, i)}
                      onDrop={(e) => handleGalleryDrop(e, i)}
                      onDragEnd={handleGalleryDragEnd}
                    >
                      <img src={url} alt={`Gallery ${i + 1}`} />
                      <button className="mw-gallery-remove" onClick={() => removeGalleryImage(url)}>×</button>
                    </div>
                  ))}
                  <div className="mw-gallery-add" onClick={() => galleryRef.current?.click()}>
                    {uploading.gallery ? "Uploading…" : "+ Add photos"}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "contact" && (
            <div className="mw-form">
              <div className="mw-field">
                <label>Contact Email</label>
                <input type="email" value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} placeholder="studio@example.com" />
              </div>
              <div className="mw-field">
                <label>Contact Phone</label>
                <input type="tel" value={form.contactPhone} onChange={(e) => setForm({ ...form, contactPhone: e.target.value })} placeholder="+91 98765 43210" />
              </div>
              <div className="mw-field">
                <label>WhatsApp Number <span style={{fontWeight:400,color:"#999",fontSize:"12px"}}>(shown as enquiry button on your site)</span></label>
                <div className="mw-wa-row">
                  <select
                    className="mw-wa-code"
                    value={form.whatsappCountryCode}
                    onChange={(e) => setForm({ ...form, whatsappCountryCode: e.target.value })}
                  >
                    {WA_CODES.map((c) => (
                      <option key={c.code} value={c.code}>{c.flag} +{c.code} {c.name}</option>
                    ))}
                  </select>
                  <input
                    className="mw-wa-number"
                    type="tel"
                    value={form.whatsappPhone}
                    onChange={(e) => setForm({ ...form, whatsappPhone: e.target.value })}
                    placeholder="98765 43210"
                  />
                </div>
              </div>
              <div className="mw-field">
                <label>Instagram URL</label>
                <input value={form.socialInstagram} onChange={(e) => setForm({ ...form, socialInstagram: e.target.value })} placeholder="https://instagram.com/yourstudio" />
              </div>
              <div className="mw-field">
                <label>Facebook URL</label>
                <input value={form.socialFacebook} onChange={(e) => setForm({ ...form, socialFacebook: e.target.value })} placeholder="https://facebook.com/yourstudio" />
              </div>
            </div>
          )}

          {activeTab === "template" && (
            <div className="mw-template-switcher">
              <p className="mw-template-hint">Switching template keeps all your content — only the design changes.</p>
              <div className="mw-template-grid">
                {INHOUSE_TEMPLATES.map((t) => (
                  <div
                    key={t.slug}
                    className={`mw-template-card ${form.templateSlug === t.slug ? "mw-template-selected" : ""}`}
                    onClick={() => setForm({ ...form, templateSlug: t.slug })}
                  >
                    <div className="mw-template-swatch">
                      {t.palette.map((c, i) => (
                        <div key={i} className="mw-swatch-block" style={{ background: c }} />
                      ))}
                      {form.templateSlug === t.slug && <div className="mw-template-check">✓</div>}
                    </div>
                    <div className="mw-template-label">
                      <strong>{t.name}</strong>
                      <span>{t.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mw-template-active">
                Active: <strong>{currentTpl?.name || form.templateSlug}</strong> — hit Save Changes to apply.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderLegacyWP = () => (
    <div className="mw-legacy">
      <div className="mw-legacy-card">
        <div className="mw-legacy-icon">🌐</div>
        <div className="mw-legacy-info">
          <h2>{wpData?.domain}</h2>
          <p className="mw-legacy-note">WordPress-powered website</p>
          <div className="mw-site-actions">
            <a href={wpData?.siteUrl} target="_blank" rel="noopener noreferrer" className="mw-btn mw-btn-primary">
              View Website
            </a>
            <button className="mw-btn mw-btn-secondary" onClick={handleWpEdit} disabled={openingEditor}>
              {openingEditor ? "Opening…" : "Edit Content"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="dashboard-container">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="main-content">
        <header className="dashboard-header">
          <div className="header-left">
            <button className="toggle-sidebar" onClick={() => setSidebarOpen(!sidebarOpen)}>☰</button>
            <h1 className="page-title">My Website</h1>
          </div>
        </header>
        <main className="dashboard-main">
          <div className="mw-container">
            {message && <div className="mw-message mw-message-error">{message}</div>}
            {loading ? (
              <div className="mw-loading">Loading…</div>
            ) : mode === "inhouse" ? renderEditor()
              : mode === "wp" ? renderLegacyWP()
              : renderTemplatePicker()}
          </div>
        </main>
      </div>
    </div>
  );
}

export default MyWebsite;
